import React, {useEffect, useMemo, useState, useRef} from 'react';
import {createAccountAction} from '../../redux/toolkit/createAccountSlice';
import {Link} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import Select from 'react-select';
import countryList from 'react-select-country-list';
import ReCAPTCHA from "react-google-recaptcha";
import Loading from "../../helper/Loading";
import {BLOCKED_EMAIL_MESSAGE, isBlockedEmailDomain} from '../../helper/blockedEmailDomains';
import c from './createaccount.module.css';

let regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d!@#$%^&*-]{6,}$/

const CreateAccountContainer = () => {

    const dispatch = useDispatch();
    const createAccount = useSelector(state => state?.createAccountReducer);
    const {loading, error, createAccountResult} = createAccount;

    const options = useMemo(() => countryList().getData(), []);

    const [mistake, setMistake] = useState('');
    const [passIsValid, setPassIsValid] = useState(false);
    const [validCaptcha, setValidCaptcha] = useState(false);
    const recaptchaRef = useRef(null);
    const [createAccountForm, setCreateAccountForm] = useState({
        company: '',
        company_address: '',
        first_name: '',
        last_name: '',
        password: '',
        repeat_password: '',
        position: '',
        username: '',
        country: '',
    });

    // Only once there is a domain to judge - flagging half-typed addresses would
    // put a red line under the field on the way to a perfectly good one.
    const emailBlocked = useMemo(
        () => isBlockedEmailDomain(createAccountForm.username),
        [createAccountForm.username]);

    useEffect(() => {
        if (createAccountForm.password && regex.test(createAccountForm.password)) {
            setPassIsValid(true);
            setMistake('');
        } else {
            setPassIsValid(false);
        }
    }, [createAccountForm.password]);


    const onChangeCaptcha = (value) => {
        // reCAPTCHA v2 tokens can be 200–2500+ characters; only reject obviously invalid lengths
        if (value && typeof value === 'string') {
            if (value.length > 5000) {
                setValidCaptcha(null);
                setMistake('reCAPTCHA token appears corrupted. Please refresh the page and try again.');
                return;
            }
            if (value.length < 20) {
                setValidCaptcha(null);
                setMistake('Invalid reCAPTCHA token. Please try again.');
                return;
            }
        }
        setValidCaptcha(value);
    }

    const resetRecaptcha = () => {
        if (recaptchaRef.current) {
            recaptchaRef.current.reset();
            setValidCaptcha(false);
        }
    }

    const handelChange = (e, name) => {
        if (name && name === 'country') {
            setCreateAccountForm({...createAccountForm, country: e.label})
        }
        if (!name || name !== 'country') {
            setCreateAccountForm({...createAccountForm, [e.target.name]: e.target.value})
        }
        if (mistake) {
            setMistake('');
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault();

        if (validCaptcha && validCaptcha.length > 5000) {
            setMistake('reCAPTCHA token appears corrupted. Please try refreshing the page and completing the captcha again.');
            return;
        }

        // Checked before the request so the user is not charged a round trip and
        // a spent reCAPTCHA token for an address the backend will refuse anyway.
        if (isBlockedEmailDomain(createAccountForm.username)) {
            setMistake(BLOCKED_EMAIL_MESSAGE);
            return;
        }

        if (createAccountForm.password === createAccountForm.repeat_password && passIsValid && validCaptcha) {
            setMistake('');
            const { repeat_password, ...formData } = createAccountForm;
            const backendFormData = {
                ...formData,
                tax_number: null,
                recaptchaToken: validCaptcha
            };
            
            // Track form submission in Google Analytics
            // eslint-disable-next-line no-undef
            if (typeof gtag !== 'undefined') {
                // eslint-disable-next-line no-undef
                gtag('event', 'form_submit', {
                    form_name: 'create_account',
                    page_path: window.location.pathname
                });
            }
            
            dispatch(createAccountAction({createAccountForm: backendFormData}));
        } else if (createAccountForm.password !== createAccountForm.repeat_password && passIsValid) {
            setMistake("Passwords don't match, please try again");
        } else if (!validCaptcha) {
            setMistake('Please complete the reCAPTCHA verification');
        } else {
            setMistake('Password must be at least 6 characters, including at least one number, one uppercase, and one lowercase letter. The password may also include the following special characters @#$%^&*-');
        }
    }

    return (
        <div className={c.createAccountContainer}>
            <div className={c.createAccountContent}>
                <div className={c.buttonContainerCA}>
                    <div className={c.loginBtn}>
                        <Link to='/'>
                            <button>Login</button>
                        </Link>
                    </div>
                    <div className={c.createAccountBtn}>
                        <Link to='/createAccount'>
                            <button>Sign Up</button>
                        </Link>
                    </div>
                </div>
                <div className={c.formCA}>
                    <form onSubmit={handleSubmit}>
                        <div className={c.formParts}>
                            <div className={c.formLeftPart}>

                                <div className={c.inpElem}>
                                    <input value={createAccountForm.first_name || ''}
                                           placeholder='First Name *'
                                           name='first_name'
                                           type='text'
                                           onChange={(e) => handelChange(e)}
                                           className={c.inputField}
                                           required/>
                                </div>
                                <div className={c.inpElem}>
                                    <input value={createAccountForm.last_name || ''}
                                           placeholder='Last Name *'
                                           name='last_name'
                                           type='text'
                                           className={c.inputField}
                                           onChange={(e) => handelChange(e)}
                                           required/>
                                </div>
                                <div className={c.inpElem}>
                                    <input value={createAccountForm.username || ''}
                                           placeholder='Email *'
                                           name='username'
                                           type='text'
                                           className={c.inputField}
                                           onChange={(e) => handelChange(e)}
                                           required/>
                                    {emailBlocked &&
                                        <span className={c.fieldHint}>{BLOCKED_EMAIL_MESSAGE}</span>}
                                </div>
                                <div className={c.inpElem}>
                                    <Select required
                                            options={options}
                                            value={createAccountForm.country ? {label: createAccountForm.country} : ''}
                                            placeholder={'Select Country *'}
                                            onChange={(e) => handelChange(e, 'country')}/>
                                </div>
                            </div>

                            <div className={c.formRightPart}>
                                <div className={c.inpElem}>
                                    <input value={createAccountForm.company || ''}
                                           placeholder='Company *'
                                           name='company'
                                           type='text'
                                           className={c.inputField}
                                           onChange={(e) => handelChange(e)}
                                           required/>
                                </div>
                                <div className={c.inpElem}>
                                    <input value={createAccountForm.company_address || ''}
                                           placeholder='Company Address'
                                           name='company_address'
                                           type='text'
                                           className={c.inputField}
                                           onChange={(e) => handelChange(e)}/>
                                </div>
                                <div className={c.inpElem}>
                                    <input value={createAccountForm.position || ''}
                                           placeholder='Position *'
                                           name='position'
                                           type='text'
                                           className={c.inputField}
                                           onChange={(e) => handelChange(e)}
                                           required/>
                                </div>
                            </div>
                        </div>
                        <div className={c.inpElem} style={{paddingTop: '25px'}}>
                            <input value={createAccountForm.password || ''}
                                   placeholder='Password *'
                                   name='password'
                                   type='password'
                                   autoComplete='new-password'
                                   className={c.inputField}
                                   onChange={(e) => handelChange(e)}
                                   required/>
                        </div>
                        <div className={c.inpElem}>
                            <input value={createAccountForm.repeat_password || ''}
                                   name='repeat_password'
                                   placeholder='Repeat Password *'
                                   type='password'
                                   autoComplete='new-password'
                                   className={c.inputField}
                                   onChange={(e) => handelChange(e)}
                                   required/>
                        </div>
                        {
                            !loading &&
                            <div className={c.submitBtnCA}>
                                <button disabled={!validCaptcha} style={{cursor: !validCaptcha ? 'not-allowed' : 'pointer'}}
                                        title={!validCaptcha ? 'Please select captcha' : 'Create Account'}
                                        type='submit'>
                                    Create Account
                                </button>
                                <div className={c.reCAPTCHAContainer}>
                                    {
                                        (!loading && !mistake && !error?.message) &&
                                        <ReCAPTCHA ref={recaptchaRef}
                                                   sitekey={window.REACT_APP_SITE_KEY}
                                                   onChange={onChangeCaptcha}
                                                   onExpired={resetRecaptcha}
                                                   onErrored={() => {
                                                       console.error('reCAPTCHA error occurred');
                                                       setValidCaptcha(false);
                                                       setMistake('reCAPTCHA error occurred. Please try again.');
                                                   }}
                                        />
                                    }
                                </div>
                            </div>
                        }
                        {
                            mistake && <span className={c.mistakeSpan}> {mistake} </span>
                        }
                        {
                            (!loading && error?.message) && <span className={c.mistakeSpan}> {error?.message} </span>
                        }
                        {
                            (!loading && !error?.message && !mistake && createAccountResult?.message) &&
                            <span className={c.accountCreated}> {createAccountResult?.message} </span>
                        }
                        {
                            loading && <Loading/>
                        }
                    </form>
                </div>
            </div>

            {/* Outside the card - the wordmark is white, so it needs the dark
                page behind it rather than the card. */}
            <div className={c.loginFormFooter}>
                <a rel='noreferrer' href='https://www.toxometris.ai/' target='_blank'>
                    <img src={'/img/Logo2.png'} alt='Toxometris.ai'/>
                </a>
            </div>
        </div>
    );
};
export default CreateAccountContainer;


